/** ZOOM-08 — leituras de aulas ao vivo do aluno/instrutor (e do admin entrando como host). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { isWaitingForHost } from './liveSessionsFormat'
import type { LiveSessionJoinConfig, LiveSessionListScope, LiveSessionRecordingSyncResponse } from '@/types/api'

export const LIVE_SESSIONS_KEY = ['live_sessions'] as const

export function liveSessionsListKey(scope: LiveSessionListScope) {
  return [...LIVE_SESSIONS_KEY, 'list', scope] as const
}

export function liveSessionKey(id: string) {
  return [...LIVE_SESSIONS_KEY, 'detail', id] as const
}

export function useLiveSessions(scope: LiveSessionListScope) {
  return useQuery({
    queryKey: liveSessionsListKey(scope),
    queryFn: () => api.liveSessions.list(scope),
    select: (response) => response.live_sessions,
  })
}

/** R10-10 — intervalo de recarga da sala enquanto o host nao inicia a aula. */
export const WAITING_FOR_HOST_POLL_MS = 20_000

export interface UseLiveSessionOptions {
  /** Recarrega a aula enquanto ela espera o host iniciar (status `scheduled`). */
  pollWhileWaiting?: boolean
}

export function useLiveSession(id: string, options: UseLiveSessionOptions = {}) {
  const { pollWhileWaiting = false } = options
  return useQuery({
    queryKey: liveSessionKey(id),
    queryFn: () => api.liveSessions.get(id),
    select: (response) => response.live_session,
    enabled: id.length > 0,
    // 404 (fora da visibilidade) e 503 (flag desligada) nao melhoram com retry.
    retry: false,
    refetchInterval: (query) => {
      const session = query.state.data?.live_session
      return pollWhileWaiting && session && isWaitingForHost(session) ? WAITING_FOR_HOST_POLL_MS : false
    },
  })
}

/**
 * Credenciais do SDK. Mutation (e nao query) de proposito: a assinatura vale 2 h e o ZAK do
 * host e de uso curto, entao cada clique em "Entrar" busca de novo e nada fica em cache.
 */
export function useJoinLiveSession(id: string) {
  return useMutation<LiveSessionJoinConfig, unknown, void>({
    mutationFn: async () => (await api.liveSessions.join(id)).join,
  })
}

/**
 * R10-13 — pede a API para buscar a gravacao na Zoom. Atualiza o detalhe em cache com a aula
 * devolvida e invalida as listas (a aba "Anteriores" passa a mostrar o link).
 */
export function useSyncLiveSessionRecording(id: string) {
  const queryClient = useQueryClient()
  return useMutation<LiveSessionRecordingSyncResponse, unknown, void>({
    mutationFn: () => api.liveSessions.syncRecording(id),
    onSuccess: (response) => {
      queryClient.setQueryData(liveSessionKey(id), { success: true, live_session: response.live_session })
      void queryClient.invalidateQueries({ queryKey: [...LIVE_SESSIONS_KEY, 'list'] })
    },
  })
}
